import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import StaffHeader, { MY_ROUTES_LINKS } from '../components/StaffHeader';
import { inZone } from '../utils/companyDate';
import usePullToRefresh from '../hooks/usePullToRefresh';
import { PRODUCT_TYPE } from '../config/product';
import { companyDateRange } from '../utils/companyRange';

function MyRoutes() {
  const { user } = useAuth();
  const timezone = user && user.timezone;

  const [routes, setRoutes] = useState([]);
  const [stats, setStats] = useState({
    total_routes: 0,
    completed_routes: 0,
    total_stops: 0,
    completed_stops: 0,
    routes_this_week: 0,
    routes_this_month: 0,
    total_earnings: 0,
    earnings_this_week: 0,
    earnings_this_month: 0,
    earnings_last_month: 0,
  });
  const [loading, setLoading] = useState(true);

  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all'); // 'all', 'this_week', 'this_month', 'last_month', 'custom'
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');

  // Detail view
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [routeDetail, setRouteDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Calendar days in the company's time zone (utils/companyRange).
  const getDateRange = useCallback(() => {
    let dateFrom = '';
    let dateTo = '';

    switch (dateFilter) {
      case 'this_week':
      case 'last_month': {
        const range = companyDateRange(dateFilter, timezone);
        dateFrom = range.start;
        dateTo = range.end;
        break;
      }
      case 'this_month': {
        const range = companyDateRange('month_to_date', timezone);
        dateFrom = range.start;
        dateTo = range.end;
        break;
      }
      case 'custom':
        dateFrom = customDateFrom;
        dateTo = customDateTo;
        break;
      default:
        break;
    }

    return { dateFrom, dateTo };
  }, [dateFilter, customDateFrom, customDateTo, timezone]);

  const loadData = useCallback(async () => {
    try {
      const { dateFrom, dateTo } = getDateRange();
      const params = new URLSearchParams();
      params.append('product', PRODUCT_TYPE);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (dateFrom) params.append('dateFrom', dateFrom);
      if (dateTo) params.append('dateTo', dateTo);

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/my-routes?${params.toString()}`
      );
      setRoutes(response.data.routes);
      setStats(response.data.stats);
    } catch (err) {
      console.error('Failed to load routes:', err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, getDateRange]);

  const { PullToRefreshIndicator } = usePullToRefresh(loadData);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const loadRouteDetail = async (routeId) => {
    setLoadingDetail(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/my-routes/${routeId}`
      );
      setRouteDetail(response.data);
      setSelectedRoute(routeId);
    } catch (err) {
      console.error('Failed to load route detail:', err);
    } finally {
      setLoadingDetail(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', inZone(timezone, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }));
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', inZone(timezone, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }));
  };

  const getStatusBadge = (status, isArchived) => {
    if (isArchived) return 'status-badge archived';
    switch (status) {
      case 'completed': return 'status-badge completed';
      case 'in_progress': return 'status-badge in-progress';
      default: return 'status-badge not-started';
    }
  };

  const getStatusText = (status, isArchived) => {
    if (isArchived) return 'Archived';
    switch (status) {
      case 'completed': return 'Completed';
      case 'in_progress': return 'In Progress';
      default: return 'Not Started';
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="my-routes-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading your routes...</p>
        </div>
        <style>{styles}</style>
      </div>
    );
  }

  return (
    <div className="my-routes-page">
      <PullToRefreshIndicator />

      {/* Header */}
      <StaffHeader active="my-routes" links={MY_ROUTES_LINKS} adminLink={false} />

      <main className="main-content">
        {/* Page Title */}
        <div className="page-header">
          <h2>My Route History</h2>
          <button className="btn btn-outline print-btn no-print" onClick={handlePrint}>
            Print
          </button>
        </div>

        {/* Stats Summary */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-value">{stats.completed_routes || 0}</div>
            <div className="stat-label">Routes Completed</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.completed_stops || 0}</div>
            <div className="stat-label">Stops Completed</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.routes_this_week || 0}</div>
            <div className="stat-label">This Week</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.routes_this_month || 0}</div>
            <div className="stat-label">This Month</div>
          </div>
        </div>

        {/* Earnings Summary */}
        {(parseFloat(stats.total_earnings) > 0 || parseFloat(stats.earnings_this_month) > 0) && (
          <div className="earnings-section">
            <h3 className="earnings-title">Earnings</h3>
            <div className="earnings-grid">
              <div className="earnings-card">
                <div className="earnings-value">${parseFloat(stats.earnings_this_week || 0).toFixed(2)}</div>
                <div className="earnings-label">This Week</div>
              </div>
              <div className="earnings-card">
                <div className="earnings-value">${parseFloat(stats.earnings_this_month || 0).toFixed(2)}</div>
                <div className="earnings-label">This Month</div>
              </div>
              <div className="earnings-card">
                <div className="earnings-value">${parseFloat(stats.earnings_last_month || 0).toFixed(2)}</div>
                <div className="earnings-label">Last Month</div>
              </div>
              <div className="earnings-card total">
                <div className="earnings-value">${parseFloat(stats.total_earnings || 0).toFixed(2)}</div>
                <div className="earnings-label">Total Earnings</div>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="filters-section no-print">
          <div className="filter-group">
            <label>Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Date Range</label>
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Time</option>
              <option value="this_week">This Week</option>
              <option value="this_month">This Month</option>
              <option value="last_month">Last Month</option>
              <option value="custom">Custom</option>
            </select>
          </div>

          {dateFilter === 'custom' && (
            <>
              <div className="filter-group">
                <label>From</label>
                <input
                  type="date"
                  value={customDateFrom}
                  onChange={(e) => setCustomDateFrom(e.target.value)}
                  className="filter-input"
                />
              </div>
              <div className="filter-group">
                <label>To</label>
                <input
                  type="date"
                  value={customDateTo}
                  onChange={(e) => setCustomDateTo(e.target.value)}
                  className="filter-input"
                />
              </div>
            </>
          )}
        </div>

        {/* Routes List */}
        {selectedRoute && routeDetail ? (
          <div className="route-detail-section">
            <button className="btn btn-outline back-btn no-print" onClick={() => setSelectedRoute(null)}>
              &larr; Back to List
            </button>

            <div className="route-detail-header">
              <h3>{routeDetail.name}</h3>
              <span className={getStatusBadge(routeDetail.status, routeDetail.is_archived)}>
                {getStatusText(routeDetail.status, routeDetail.is_archived)}
              </span>
            </div>

            <div className="route-detail-meta">
              <p><strong>Date:</strong> {formatDate(routeDetail.created_at)}</p>
              <p><strong>Service Type:</strong> {routeDetail.service_type === 'lawn_care' ? 'Lawn Care' : 'Snow Removal'}</p>
              {routeDetail.starting_address && (
                <p><strong>Starting Location:</strong> {routeDetail.starting_address}, {routeDetail.starting_city}</p>
              )}
              {routeDetail.driver_pay_type && (
                <div className="route-detail-pay">
                  <strong>Your Pay:</strong>{' '}
                  {routeDetail.driver_pay_total ? (
                    <span className="pay-total">${parseFloat(routeDetail.driver_pay_total).toFixed(2)}</span>
                  ) : routeDetail.driver_pay_rate ? (
                    <span className="pay-rate-detail">
                      {routeDetail.driver_pay_type === 'flat' && `$${parseFloat(routeDetail.driver_pay_rate).toFixed(2)} (Flat Rate)`}
                      {routeDetail.driver_pay_type === 'hourly' && `$${parseFloat(routeDetail.driver_pay_rate).toFixed(2)}/hr${routeDetail.driver_hours_worked ? ` × ${routeDetail.driver_hours_worked}hrs = $${(parseFloat(routeDetail.driver_pay_rate) * parseFloat(routeDetail.driver_hours_worked)).toFixed(2)}` : ''}`}
                      {routeDetail.driver_pay_type === 'per_stop' && `$${parseFloat(routeDetail.driver_pay_rate).toFixed(2)}/stop`}
                    </span>
                  ) : (
                    <span className="pay-not-set">Not set</span>
                  )}
                </div>
              )}
            </div>

            <h4>Stops ({routeDetail.stops?.length || 0})</h4>
            <div className="stops-list">
              {routeDetail.stops?.map((stop, index) => (
                <div key={stop.id} className={`stop-card ${stop.status}`}>
                  <div className="stop-header">
                    <span className="stop-number">#{index + 1}</span>
                    <span className={`stop-status ${stop.status}`}>
                      {stop.status === 'completed' ? 'Completed' : stop.status === 'skipped' ? 'Skipped' : 'Pending'}
                    </span>
                  </div>
                  <div className="stop-content">
                    <p className="stop-name">{stop.first_name} {stop.last_name}</p>
                    <p className="stop-address">{stop.address}, {stop.city}, {stop.state} {stop.zip}</p>

                    {stop.status === 'completed' && (
                      <div className="stop-details">
                        <p><strong>Completed:</strong> {formatDateTime(stop.completed_at)}</p>
                        {routeDetail.service_type !== 'lawn_care' && (
                          <>
                            {stop.salt_applied !== null && (
                              <p><strong>Salt:</strong> {stop.salt_applied ? 'Applied' : 'Not Applied'}</p>
                            )}
                            {stop.snow_depth && (
                              <p><strong>Snow Depth:</strong> {stop.snow_depth}"</p>
                            )}
                          </>
                        )}
                        {routeDetail.service_type === 'lawn_care' && stop.checklist_items_completed && (
                          <p><strong>Services:</strong> {
                            Array.isArray(stop.checklist_items_completed)
                              ? stop.checklist_items_completed.join(', ')
                              : stop.checklist_items_completed
                          }</p>
                        )}
                        {stop.completion_notes && (
                          <p><strong>Notes:</strong> {stop.completion_notes}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="routes-list">
            {routes.length === 0 ? (
              <div className="empty-state">
                <p>No routes found for the selected filters.</p>
              </div>
            ) : (
              routes.map((route) => (
                <div
                  key={route.id}
                  className="route-card"
                  onClick={() => loadRouteDetail(route.id)}
                >
                  <div className="route-card-header">
                    <h3 className="route-name">{route.name}</h3>
                    <span className={getStatusBadge(route.status, route.is_archived)}>
                      {getStatusText(route.status, route.is_archived)}
                    </span>
                  </div>
                  <div className="route-card-body">
                    <p className="route-date">{formatDate(route.created_at)}</p>
                    <div className="route-stats">
                      <span className="route-stat">
                        <strong>{route.stop_count || 0}</strong> stops
                      </span>
                      <span className="route-stat">
                        <strong>{route.completed_count || 0}</strong> completed
                      </span>
                      {parseInt(route.skipped_count) > 0 && (
                        <span className="route-stat skipped">
                          <strong>{route.skipped_count}</strong> skipped
                        </span>
                      )}
                    </div>
                    {/* Driver Pay Display */}
                    {route.driver_pay_type && (
                      <div className="route-pay">
                        {route.driver_pay_total ? (
                          <span className="pay-amount">Your Pay: ${parseFloat(route.driver_pay_total).toFixed(2)}</span>
                        ) : route.driver_pay_rate ? (
                          <span className="pay-rate">
                            {route.driver_pay_type === 'flat' && `Flat Rate: $${parseFloat(route.driver_pay_rate).toFixed(2)}`}
                            {route.driver_pay_type === 'hourly' && `$${parseFloat(route.driver_pay_rate).toFixed(2)}/hr`}
                            {route.driver_pay_type === 'per_stop' && `$${parseFloat(route.driver_pay_rate).toFixed(2)}/stop`}
                          </span>
                        ) : (
                          <span className="pay-pending">Pay not set</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {loadingDetail && (
          <div className="loading-overlay">
            <div className="loading-spinner"></div>
          </div>
        )}
      </main>

      <style>{styles}</style>
    </div>
  );
}

const styles = `
  .my-routes-page {
    min-height: 100vh;
    background: #f3f4f6;
  }

  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    padding: 20px;
  }

  .loading-spinner {
    width: 40px;
    height: 40px;
    border: 3px solid #e5e7eb;
    border-top-color: #3b82f6;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 16px;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  /* Header */
  .app-header {
    background: linear-gradient(135deg, #1e3a5f 0%, #0f1f33 100%);
    color: white;
    padding: 16px 24px;
    position: sticky;
    top: 0;
    z-index: 100;
  }

  .app-header-content {
    max-width: 1200px;
    margin: 0 auto;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .app-logo {
    font-size: 1.5rem;
    font-weight: 700;
    margin: 0;
    cursor: pointer;
  }

  .desktop-nav {
    display: flex;
    gap: 8px;
  }

  .nav-link {
    background: rgba(255,255,255,0.1);
    border: none;
    color: white;
    padding: 8px 16px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.875rem;
    transition: background 0.15s;
  }

  .nav-link:hover, .nav-link.active {
    background: rgba(255,255,255,0.2);
  }

  .nav-link.storm-link {
    background: rgba(239, 68, 68, 0.3);
  }

  .nav-link.logout-link {
    background: rgba(239, 68, 68, 0.2);
  }

  .mobile-nav {
    display: none;
    flex-direction: column;
    gap: 8px;
    padding-top: 16px;
    border-top: 1px solid rgba(255,255,255,0.1);
    margin-top: 16px;
  }

  .mobile-nav-link {
    background: rgba(255,255,255,0.1);
    border: none;
    color: white;
    padding: 12px 16px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 1rem;
    text-align: left;
  }

  @media (max-width: 900px) {
    .desktop-nav {
      display: none;
    }
    .mobile-nav {
      display: flex;
    }
  }

  /* Main Content */
  .main-content {
    max-width: 1200px;
    margin: 0 auto;
    padding: 24px;
  }

  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
  }

  .page-header h2 {
    margin: 0;
    color: #1f2937;
  }

  /* Stats Grid */
  .stats-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
    margin-bottom: 24px;
  }

  @media (max-width: 768px) {
    .stats-grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  .stat-card {
    background: white;
    border-radius: 12px;
    padding: 20px;
    text-align: center;
    box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  }

  .stat-value {
    font-size: 2rem;
    font-weight: 700;
    color: #1e3a5f;
  }

  .stat-label {
    font-size: 0.875rem;
    color: #6b7280;
    margin-top: 4px;
  }

  /* Earnings Section */
  .earnings-section {
    background: linear-gradient(135deg, #059669 0%, #047857 100%);
    border-radius: 12px;
    padding: 20px;
    margin-bottom: 24px;
    color: white;
  }

  .earnings-title {
    margin: 0 0 16px 0;
    font-size: 1.1rem;
    font-weight: 600;
    opacity: 0.9;
  }

  .earnings-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
  }

  @media (max-width: 768px) {
    .earnings-grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  .earnings-card {
    background: rgba(255,255,255,0.15);
    border-radius: 8px;
    padding: 16px;
    text-align: center;
  }

  .earnings-card.total {
    background: rgba(255,255,255,0.25);
  }

  .earnings-value {
    font-size: 1.5rem;
    font-weight: 700;
  }

  .earnings-label {
    font-size: 0.8rem;
    opacity: 0.85;
    margin-top: 4px;
  }

  /* Route Pay Display */
  .route-pay {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid #e5e7eb;
  }

  .pay-amount {
    font-size: 1.1rem;
    font-weight: 700;
    color: #059669;
  }

  .pay-rate {
    font-size: 0.9rem;
    color: #6b7280;
  }

  .pay-pending {
    font-size: 0.9rem;
    color: #9ca3af;
    font-style: italic;
  }

  /* Route Detail Pay */
  .route-detail-pay {
    margin-top: 12px;
    padding: 12px 16px;
    background: rgba(5, 150, 105, 0.1);
    border-radius: 8px;
    border-left: 4px solid #059669;
  }

  .pay-total {
    font-size: 1.25rem;
    font-weight: 700;
    color: #059669;
  }

  .pay-rate-detail {
    color: #059669;
  }

  .pay-not-set {
    color: #9ca3af;
    font-style: italic;
  }

  /* Filters */
  .filters-section {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    margin-bottom: 24px;
    padding: 16px;
    background: white;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  }

  .filter-group {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .filter-group label {
    font-size: 0.75rem;
    font-weight: 500;
    color: #6b7280;
    text-transform: uppercase;
  }

  .filter-select, .filter-input {
    padding: 8px 12px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 0.875rem;
    min-width: 140px;
  }

  /* Routes List */
  .routes-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .route-card {
    background: white;
    border-radius: 12px;
    padding: 16px 20px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.06);
    cursor: pointer;
    transition: transform 0.15s, box-shadow 0.15s;
  }

  .route-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  }

  .route-card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
  }

  .route-name {
    margin: 0;
    font-size: 1.1rem;
    color: #1f2937;
  }

  .route-date {
    margin: 0 0 8px 0;
    font-size: 0.875rem;
    color: #6b7280;
  }

  .route-stats {
    display: flex;
    gap: 16px;
  }

  .route-stat {
    font-size: 0.875rem;
    color: #4b5563;
  }

  .route-stat.skipped {
    color: #dc2626;
  }

  /* Status Badges */
  .status-badge {
    display: inline-block;
    padding: 4px 10px;
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: 600;
  }

  .status-badge.completed {
    background: #d1fae5;
    color: #065f46;
  }

  .status-badge.in-progress {
    background: #dbeafe;
    color: #1e40af;
  }

  .status-badge.not-started {
    background: #f3f4f6;
    color: #6b7280;
  }

  .status-badge.archived {
    background: #fef3c7;
    color: #92400e;
  }

  /* Route Detail */
  .route-detail-section {
    background: white;
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  }

  .back-btn {
    margin-bottom: 16px;
  }

  .route-detail-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    padding-bottom: 16px;
    border-bottom: 1px solid #e5e7eb;
  }

  .route-detail-header h3 {
    margin: 0;
    font-size: 1.5rem;
    color: #1f2937;
  }

  .route-detail-meta {
    margin-bottom: 24px;
  }

  .route-detail-meta p {
    margin: 4px 0;
    color: #4b5563;
  }

  /* Stops List */
  .stops-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .stop-card {
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 16px;
    background: #f9fafb;
  }

  .stop-card.completed {
    border-left: 4px solid #22c55e;
  }

  .stop-card.skipped {
    border-left: 4px solid #ef4444;
    opacity: 0.7;
  }

  .stop-card.pending {
    border-left: 4px solid #9ca3af;
  }

  .stop-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
  }

  .stop-number {
    font-weight: 600;
    color: #6b7280;
  }

  .stop-status {
    font-size: 0.75rem;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 4px;
  }

  .stop-status.completed {
    background: #d1fae5;
    color: #065f46;
  }

  .stop-status.skipped {
    background: #fee2e2;
    color: #dc2626;
  }

  .stop-status.pending {
    background: #f3f4f6;
    color: #6b7280;
  }

  .stop-name {
    margin: 0;
    font-weight: 600;
    color: #1f2937;
  }

  .stop-address {
    margin: 4px 0 0 0;
    color: #6b7280;
    font-size: 0.875rem;
  }

  .stop-details {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid #e5e7eb;
  }

  .stop-details p {
    margin: 4px 0;
    font-size: 0.875rem;
    color: #4b5563;
  }

  /* Buttons */
  .btn {
    padding: 10px 20px;
    border: none;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s;
  }

  .btn-outline {
    background: white;
    border: 1px solid #d1d5db;
    color: #374151;
  }

  .btn-outline:hover {
    background: #f9fafb;
  }

  /* Empty State */
  .empty-state {
    text-align: center;
    padding: 60px 20px;
    color: #6b7280;
  }

  /* Loading Overlay */
  .loading-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255,255,255,0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
  }

  /* Print Styles */
  @media print {
    .no-print {
      display: none !important;
    }

    .app-header {
      background: white;
      color: black;
      position: static;
    }

    .app-logo {
      color: #1e3a5f;
    }

    .route-card, .stop-card, .stat-card, .route-detail-section {
      box-shadow: none;
      border: 1px solid #e5e7eb;
    }

    .route-card:hover {
      transform: none;
    }
  }
`;

export default MyRoutes;
